<script setup>
import { defineProps, computed, reactive, watch } from 'vue';
import Message from './Message.vue';
// NOTICE: KAPTION-INTEGRATION - note-cards.GROUP: back-to-back events fold into one line
import KaptionNoteCardGroup from './KaptionNoteCardGroup.vue';
import { groupTimelineEvents } from 'dashboard/helper/kaptionNoteCardGroups';
// NOTICE: KAPTION-INTEGRATION - Import UndoPendingOverlay for undo send feature
import UndoPendingOverlay from 'dashboard/components/kaption/UndoPendingOverlay.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: the held-texts bubble
import KaptionMergePendingBubble from 'dashboard/components/kaption/KaptionMergePendingBubble.vue';
import { MESSAGE_TYPES, MESSAGE_STATUS } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useUndoSend } from 'dashboard/composables/useUndoSend.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import MessageApi from 'dashboard/api/inbox/message.js';
import KaptionPendingOutboundMessage from 'dashboard/components/kaption/KaptionPendingOutboundMessage.vue';
// NOTICE: KAPTION-INTEGRATION - Durable WhatsApp outbound batches. The composable
// owns the flag + WhatsApp-channel gate that keeps this off non-batching accounts.
import { useKaptionMessageBatch } from 'dashboard/composables/useKaptionMessageBatch';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 * @property {Set} translatingMessages - Set of message IDs currently being translated
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
  // NOTICE: KAPTION-INTEGRATION - Pass translating messages to track which are being translated
  translatingMessages: {
    type: Set,
    default: () => new Set(),
  },
  // NOTICE: KAPTION-INTEGRATION - note-cards.GROUP: fold back-to-back events (the account shows
  // cards), and the message a link points to, so the group holding it opens.
  groupEvents: {
    type: Boolean,
    default: false,
  },
  linkedMessageId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - upstream #15279: keep the keys a contact
  // submitted in a WhatsApp Flow as sent (they are labels, not attributes).
  const messages = useCamelCase(props.messages, {
    deep: true,
    stopPaths: [
      'content_attributes.whatsapp_flow_response.response_json',
      // NOTICE: KAPTION-INTEGRATION - whatsapp-flows: labels and prefill data
      // are keyed by the flow's own field names.
      'content_attributes.kaption_flow.labels',
      'content_attributes.kaption_flow.fields',
      'content_attributes.kaption_whatsapp_flow.data',
    ],
  });

  return messages;
});

const currentChat = useMapGetter('getSelectedChat');

// NOTICE: KAPTION-INTEGRATION - note-cards.GROUP.1/2/8: where the account shows cards, back-to-back
// events (cards and activity lines) fold into one row; elsewhere every message is its own row.
const timelineRows = computed(() =>
  props.groupEvents
    ? groupTimelineEvents(allMessages.value)
    : allMessages.value.map((message, index) => ({
        type: 'message',
        key: `message-${message.id}`,
        message,
        index,
      }))
);

// note-cards.GROUP.5/7: groups start closed and keep their state (by their first event) while the
// list lives. A group holding the message a link points to opens once, so the link still lands.
const openGroups = reactive(new Set());
const toggleGroup = key => {
  if (openGroups.has(key)) openGroups.delete(key);
  else openGroups.add(key);
};
let linkedMessageOpened = null;
watch(
  () => [props.linkedMessageId, timelineRows.value],
  ([linkedId, rows]) => {
    if (!linkedId || linkedId === linkedMessageOpened) return;
    const group = rows.find(
      row =>
        row.type === 'group' &&
        row.items.some(item => item.message.id === linkedId)
    );
    if (!group) return;
    openGroups.add(group.key);
    linkedMessageOpened = linkedId;
  },
  { immediate: true }
);

const isGroupConversation = computed(() => currentChat.value?.group === true);

// NOTICE: KAPTION-INTEGRATION - Durable WhatsApp outbound batches
const {
  messageBatch,
  messageBatchBusy,
  flushBatch,
  discardBatch,
  updateBatch,
} = useKaptionMessageBatch();

// NOTICE: KAPTION-INTEGRATION - Undo send feature setup
const { undoPendingMessages, undoMessage } = useUndoSend();
const { uiSettings } = useUISettings();

// Get configured undo duration from settings (default 15 seconds)
const undoDuration = computed(
  () => uiSettings.value?.kaption_undo_send_duration ?? 15
);

/**
 * Checks if a message is in pending_undo status
 * @param {Object} message - The message to check
 * @returns {Boolean} - Whether the message is pending undo
 */
const isPendingUndo = message => message.status === MESSAGE_STATUS.PENDING_UNDO;

// NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: held texts share the
// PENDING_UNDO status and are marked by meta.kaptionMergeGroup.
const isKaptionMergePending = message =>
  isPendingUndo(message) && !!message.meta?.kaptionMergeGroup;

/**
 * Gets the remaining seconds for a pending undo message
 * @param {Object} message - The message to get countdown for
 * @returns {Number} - Remaining seconds (defaults to configured duration)
 */
const getUndoRemainingSeconds = message => {
  const pending = undoPendingMessages.value[message.id];
  return pending?.remainingSeconds ?? undoDuration.value;
};

/**
 * Handles undo action for a pending message
 * @param {Number|String} messageId - The ID of the message to undo
 */
const handleUndo = messageId => {
  undoMessage(messageId);
};

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    // NOTICE: KAPTION-INTEGRATION - message-pagination.DASHBOARD.6: [id, id + 1) is exactly
    // this message under both the date-slice and the legacy id-range semantics of the
    // endpoint; an id +/- 100 probe can miss rows whose date and id disagree.
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      after: messageId,
      before: messageId + 1,
      limit: 1,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

// NOTICE: KAPTION-INTEGRATION - Check if the previous message has the same sender.
// Looser than shouldGroupWithNext — no time constraint, just same sender + same type.
// Used to suppress sender name on consecutive messages from the same sender (WhatsApp-style).
const hasSameSenderAsPrevious = (index, searchList) => {
  if (index === 0) return false;

  const current = searchList[index];
  const prev = searchList[index - 1];

  if (current.status === 'failed' || prev.status === 'failed') return false;

  const currentSenderId = current.senderId ?? current.sender?.id;
  const prevSenderId = prev.senderId ?? prev.sender?.id;
  if (currentSenderId !== prevSenderId) return false;

  // Must be same direction (both incoming or both outgoing)
  return current.messageType === prev.messageType;
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4 bg-n-background">
    <slot name="beforeAll" />
    <template
      v-for="{ type, key, message, index, items, summary } in timelineRows"
      :key="key"
    >
      <!-- NOTICE: KAPTION-INTEGRATION - note-cards.GROUP: a group of back-to-back events -->
      <template v-if="type === 'group'">
        <slot
          v-if="
            firstUnreadId &&
            items.some(item => item.message.id === firstUnreadId)
          "
          name="unreadBadge"
        />
        <KaptionNoteCardGroup
          :summary="summary"
          :is-open="openGroups.has(key)"
          @toggle="toggleGroup(key)"
        >
          <Message
            v-for="item in items"
            :key="item.message.id"
            v-bind="item.message"
            :is-email-inbox="isAnEmailChannel"
            :is-group-conversation="isGroupConversation"
            :in-reply-to="getInReplyToMessage(item.message)"
            :group-with-next="shouldGroupWithNext(item.index, allMessages)"
            :group-with-previous="
              hasSameSenderAsPrevious(item.index, allMessages)
            "
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            :is-message-translating="
              props.translatingMessages.has(item.message.id)
            "
            data-clarity-mask="True"
            @retry="emit('retry', item.message)"
          />
        </KaptionNoteCardGroup>
      </template>
      <template v-else>
        <slot
          v-if="firstUnreadId && message.id === firstUnreadId"
          name="unreadBadge"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging -->
        <KaptionMergePendingBubble
          v-if="isKaptionMergePending(message)"
          :count="message.contentAttributes?.kaptionMerge?.sourceCount || 1"
          :paused="!!message.meta?.kaptionMergePaused"
        >
          <Message
            v-bind="message"
            :is-email-inbox="isAnEmailChannel"
            :is-group-conversation="isGroupConversation"
            :in-reply-to="getInReplyToMessage(message)"
            :group-with-next="shouldGroupWithNext(index, allMessages)"
            :group-with-previous="hasSameSenderAsPrevious(index, allMessages)"
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            :is-message-translating="props.translatingMessages.has(message.id)"
            data-clarity-mask="True"
          />
        </KaptionMergePendingBubble>
        <!-- NOTICE: KAPTION-INTEGRATION - Wrap pending_undo messages with UndoPendingOverlay -->
        <UndoPendingOverlay
          v-else-if="isPendingUndo(message)"
          :remaining-seconds="getUndoRemainingSeconds(message)"
          :total-duration="undoDuration"
          @undo="handleUndo(message.id)"
        >
          <Message
            v-bind="message"
            :is-email-inbox="isAnEmailChannel"
            :is-group-conversation="isGroupConversation"
            :in-reply-to="getInReplyToMessage(message)"
            :group-with-next="shouldGroupWithNext(index, allMessages)"
            :group-with-previous="hasSameSenderAsPrevious(index, allMessages)"
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            :is-message-translating="props.translatingMessages.has(message.id)"
            data-clarity-mask="True"
            @retry="emit('retry', message)"
          />
        </UndoPendingOverlay>
        <!-- NOTICE: KAPTION-INTEGRATION - Regular messages render without wrapper -->
        <Message
          v-else
          v-bind="message"
          :is-email-inbox="isAnEmailChannel"
          :is-group-conversation="isGroupConversation"
          :in-reply-to="getInReplyToMessage(message)"
          :group-with-next="shouldGroupWithNext(index, allMessages)"
          :group-with-previous="hasSameSenderAsPrevious(index, allMessages)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          :is-message-translating="props.translatingMessages.has(message.id)"
          data-clarity-mask="True"
          @retry="emit('retry', message)"
        />
      </template>
    </template>
    <KaptionPendingOutboundMessage
      v-if="messageBatch"
      :batch="messageBatch"
      :busy="messageBatchBusy"
      @send="flushBatch"
      @discard="discardBatch"
      @update="updateBatch"
    />
    <slot name="after" />
  </ul>
</template>
